import { useState } from "react";
import { useAppContextNonNull } from "@contexts/AppContext";
import { TypeaheadFn } from "@components/shared/TypeaheadFn/TypeaheadFn";
import OwButton from "@components/shared/OwButton/OwButton";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwBadge from "@components/shared/OwBadge/OwBadge";
import OwToggle from "@components/shared/OwToggle/OwToggle";
import UnanetService from "@services/UnanetService";
import IUnanetProjectSummary from "@models/Unanet/IUnanetProjectSummary";
import IUnanetExpenseType from "@models/Unanet/IUnanetExpenseType";
import IConnection_Token from "@models/IConnection_Token";

type Props = {
    vendorToken: IConnection_Token;
    onClose: (refresh?: boolean) => void;
};

export const UnanetProjectMappingEdit = (props: Props) => {
    const { userModel, supplierModel, uiContext } = useAppContextNonNull();
    const vendor = props.vendorToken.Vendor;
    const vi = vendor?.Vendor_Integration;

    const [projects, setProjects] = useState<IUnanetProjectSummary[]>([]);
    const [selectedProject, setSelectedProject] = useState<IUnanetProjectSummary | undefined>(
        vi?.unanet_project_key != null
            ? {
                  key: vi.unanet_project_key,
                  code: vi.unanet_project_code ?? "",
                  title: vi.unanet_project_title ?? "",
                  projectType:
                      vi.unanet_project_type_key != null
                          ? { key: vi.unanet_project_type_key, name: vi.unanet_project_type_name ?? "" }
                          : undefined,
              }
            : undefined
    );
    const [expenseTypes, setExpenseTypes] = useState<IUnanetExpenseType[]>([]);
    const [selectedExpenseType, setSelectedExpenseType] = useState<IUnanetExpenseType | undefined>(
        vi?.unanet_expense_type_key != null
            ? { key: vi.unanet_expense_type_key, name: vi.unanet_expense_type_name ?? "", code: "" }
            : undefined
    );
    const [requiresTasks, setRequiresTasks] = useState(vi?.unanet_requires_tasks ?? false);
    const [isSaving, setIsSaving] = useState(false);

    if (!vendor) {
        return <div className={"p-3"}>Vendor data not available.</div>;
    }

    const lookupProjects = (value: string) => {
        if (!value || value.length < 1) {
            setProjects([]);
            return;
        }
        UnanetService.searchProjects({ search: value }, userModel, supplierModel)
            .then(setProjects)
            .catch(() => setProjects([]));
    };

    const lookupExpenseTypes = (value: string) => {
        if (!value || value.length < 1) {
            setExpenseTypes([]);
            return;
        }
        const lower = value.toLowerCase();
        UnanetService.getExpenseTypes(userModel, supplierModel)
            .then((types) =>
                setExpenseTypes(types.filter((t) => t.name.toLowerCase().includes(lower) || t.code.toLowerCase().includes(lower)))
            )
            .catch(() => setExpenseTypes([]));
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await UnanetService.updateVendorMapping(
                vendor.guid,
                {
                    unanet_project_key: selectedProject?.key ?? null,
                    unanet_project_code: selectedProject?.code ?? null,
                    unanet_project_title: selectedProject?.title ?? null,
                    unanet_project_type_key: selectedProject?.projectType?.key ?? null,
                    unanet_project_type_name: selectedProject?.projectType?.name ?? null,
                    unanet_requires_tasks: selectedProject ? requiresTasks : false,
                },
                userModel,
                supplierModel,
                vendor
            );
            await UnanetService.updateVendorExpenseType(
                vendor.guid,
                {
                    unanet_expense_type_key: selectedExpenseType?.key ?? null,
                    unanet_expense_type_name: selectedExpenseType?.name ?? null,
                },
                userModel,
                supplierModel,
                vendor
            );
            props.onClose(true);
        } catch (e) {
            uiContext.notifyFn({ message: "Failed to update vendor mapping" } as any);
        } finally {
            setIsSaving(false);
        }
    };

    const handleClear = () => {
        setSelectedProject(undefined);
        setRequiresTasks(false);
    };

    const handleClearExpenseType = () => {
        setSelectedExpenseType(undefined);
    };

    return (
        <div className={"p-3"}>
            <h5>
                <OwLabel title={"edit_unanet_project_mapping"} noBottomMargin />
            </h5>

            <div className={"mb-3"}>
                <OwLabel title={"vendor_name"} />
                <div className={"fw-bold"}>{vendor.name}</div>
            </div>

            <div className={"mb-3"}>
                <OwLabel title={"current_mapping"} />
                <div>
                    {vi?.unanet_project_key != null ? (
                        <OwBadge
                            title={`${vi.unanet_project_code} - ${vi.unanet_project_title ?? ""}`}
                            bgColor={"bg-success"}
                            skipTranslate={true}
                        />
                    ) : (
                        <OwBadge title={"unmapped"} bgColor={"bg-secondary"} />
                    )}
                </div>
            </div>

            <div className={"mb-3"}>
                <OwLabel title={"unanet_project"} />
                <TypeaheadFn
                    lookupFn={lookupProjects}
                    availableOptions={projects}
                    selectedOption={selectedProject}
                    selectedOptionFn={(item: IUnanetProjectSummary) => setSelectedProject(item)}
                    renderFn={(item: IUnanetProjectSummary) => (
                        <span>
                            {item.code} - {item.title}
                        </span>
                    )}
                    titleProp={"code"}
                    subTitleProp={"title"}
                    valueProp={"key"}
                    placeholder={"search_unanet_projects"}
                    isScrollable={true}
                    maxHeight={300}
                    usePortal={true}
                    clearSelectionFn={handleClear}
                />
            </div>

            {selectedProject && (
                <div className={"mb-3"}>
                    <OwButton title={"clear_mapping"} className={"btn btn-outline-secondary btn-sm"} fn={handleClear} />
                </div>
            )}

            <div className={"mb-3"}>
                <OwLabel title={"unanet_expense_type_override"} />
                <TypeaheadFn
                    lookupFn={lookupExpenseTypes}
                    availableOptions={expenseTypes}
                    selectedOption={selectedExpenseType}
                    selectedOptionFn={(item: IUnanetExpenseType) => setSelectedExpenseType(item)}
                    renderFn={(item: IUnanetExpenseType) => (
                        <span>
                            {item.code} - {item.name}
                        </span>
                    )}
                    titleProp={"name"}
                    valueProp={"key"}
                    placeholder={"uses_supplier_default"}
                    isScrollable={true}
                    maxHeight={300}
                    usePortal={true}
                    clearSelectionFn={handleClearExpenseType}
                />
            </div>

            {selectedExpenseType && (
                <div className={"mb-3"}>
                    <OwButton title={"clear_mapping"} className={"btn btn-outline-secondary btn-sm"} fn={handleClearExpenseType} />
                </div>
            )}

            {selectedProject && (
                <div className={"mb-3"}>
                    <OwLabel title={"unanet_requires_tasks"} />
                    <OwToggle active={requiresTasks} name={"requiresTasks"} fn={() => setRequiresTasks(!requiresTasks)} label={""} />
                </div>
            )}

            <div className={"d-flex gap-2"}>
                <OwButton title={"save"} className={"btn btn-primary"} fn={handleSave} disabled={isSaving} />
                <OwButton title={"cancel"} className={"btn btn-secondary"} fn={() => props.onClose(false)} disabled={isSaving} />
            </div>
        </div>
    );
};
